import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import { MissingAdminRouteTokenError } from "@/lib/admin-route-context-error";

// An error boundary can remount after a failed retry. Keep the attempt across
// mounts and allow a new automatic retry once the admin page loads again.
let automaticRetryAttempted = false;

export function resetAdminRouteContextRetry() {
  automaticRetryAttempted = false;
}

export function useAdminRouteContextRecoveryReady() {
  useEffect(() => {
    resetAdminRouteContextRetry();
  }, []);
}

export function useAdminRouteContextRecovery(error: Error, reset: () => void) {
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  const retry = useCallback(async () => {
    queryErrorResetBoundary.reset();
    await router.invalidate();
    reset();
  }, [queryErrorResetBoundary, reset, router]);

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  useEffect(() => {
    if (
      !(error instanceof MissingAdminRouteTokenError) ||
      automaticRetryAttempted
    ) {
      return;
    }

    automaticRetryAttempted = true;
    void retry().catch(() => {
      // Keep the error page and its manual retry available.
    });
  }, [error, retry]);

  return retry;
}
